/* eslint-disable no-unused-vars */
import React, { useEffect, useRef, useState } from 'react';
import { unparse } from 'papaparse';
import { FormikValues, useFormik } from 'formik';
import * as yup from 'yup';
import {
  onTapTapCustomerPaymentAddFromTransactionPayments,
  taptapAddCustomerFields,
  taptapGetCustomerDetailsViaTransactionNo,
  taptapStatuses
} from 'src/redux/slices/taptap-customer';
import { TapTapCustomerData } from 'src/redux/slices/taptap-customer/types';
import { cloneDeep, isEmpty } from 'lodash';
import BranchQuestionaireForm from './components/BranchQuestionaireFormV2';
import { AlertDialog, LoaderBar, LoadingButton, Page } from 'src/components';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Container,
  Divider,
  InputAdornment,
  makeStyles,
  TextField,
  Typography
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { SupabaseClientService } from 'src/services';
import { usePermissions, useSnackBar } from 'src/hooks';
import { useLocation, useNavigate } from 'react-router-dom';
import { unwrapResult } from '@reduxjs/toolkit';
import { slices, useAppDispatch } from 'src/redux';
import {
  GetTransactionPaymentsResponse,
  GetTransactionsViaTransactionNoResponse,
  TemplateDetails,
  Transaction
} from 'src/types';
import SearchIcon from '@material-ui/icons/Search';
import { taptapCustomerField } from 'src/redux/slices/taptap-customer/constants/taptap-fields';
import {
  cleanSN,
  downloadSupabaseBranchQuestionaireCSV,
  sleep
} from 'src/utils';
import PowerIcon from '@material-ui/icons/Power';
import { sortProductsByCategory } from 'src/utils/categories/sortByCategory';
import { initialValues } from './constants';
import { BranchQuestionaireFormValues } from './types';

interface FormValues {
  [key: string]: any; // could string, boolean, number, etc
}

const useStyles = makeStyles(() => ({
  root: {
    minHeight: '100%',
    paddingBottom: 20,
    paddingTop: 20
  },
  contactCard: {
    marginTop: 20
  }
}));

const { actions: pcBundleActions } = slices.pcBundles;
const { actions: quotationActions } = slices.quotations;
const { actions: createCustomerServiceActions } = slices.customerService;
const { actions: transactionActions } = slices.transaction;
const { actions: transactionPaymentActions } = slices.transactionPayments;

const AddTapTapCustomer = () => {
  const classes = useStyles();
  const snackBar = useSnackBar();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { canDownloadBranchQuestionaireCSV } = usePermissions();

  const isLoading = useRef(false);

  const [quotationItemsConcerns, setQuotationItemsConcerns] = useState<
    string[]
  >();
  const [quotationResponse, setQuotationResponse] = useState<TemplateDetails>();
  const [quotationProductsLoading, setQuotationProductsLoading] = useState<
    boolean
  >(false);

  const [transactionsInfoLoading, setTransactionsInfoLoading] = useState<
    boolean
  >(false);
  const [transactionNoInput, setTransactionNoInput] = useState<string>('');
  const [transactionInfoResponse, setTransactionInfoResponse] = useState<
    GetTransactionsViaTransactionNoResponse
  >();
  const [
    transactionPaymentsResponse,
    setTransactionPaymentsResponse
  ] = useState<GetTransactionPaymentsResponse>();

  const [poweredBuildSNInput, setPoweredBuildSNInput] = useState<string>('');
  const [poweredBuildSNResponse, setPoweredBuildSNResponse] = useState<
    GetTransactionsViaTransactionNoResponse // CHANGE THIS TO POWERED BUILD RESPONSE
  >();

  const resetFormAlongWithOtherStates = () => {
    formik.resetForm();
    setQuotationItemsConcerns(undefined);
    setQuotationResponse(undefined);

    // Transactions
    setTransactionNoInput('');
    setTransactionPaymentsResponse(undefined);
    setTransactionInfoResponse(undefined);
    setTransactionsInfoLoading(false);

    // Powered Builds
    setPoweredBuildSNInput('');
    setPoweredBuildSNResponse(undefined);
  };

  const fetchQuotationProductsAvailability = async (
    quotationResponseArg?: TemplateDetails
  ) => {
    const response = unwrapResult(
      await dispatch(
        quotationActions.checkQuotationAvailabilityThunk({
          items: quotationResponseArg?.products || [],
          branch_id: quotationResponseArg?.branch_id
        })
      )
    );

    return response;
  };

  const onBranchFieldSetBasedOnQuotation = (
    quotationResponseArg: TemplateDetails
  ) => {
    const branchBasedOnQuotation = taptapAddCustomerFields
      .find((x) => x.key === taptapCustomerField.BRANCH_RELEASED)
      ?.options?.find(
        (x: any) => x.branch_id === quotationResponseArg?.branch_id
      )?.value;

    if (branchBasedOnQuotation) {
      formik.setFieldValue(
        taptapCustomerField.BRANCH_RELEASED,
        branchBasedOnQuotation
      );
    }
  };

  const fetchQuotationProducts = async (internal_quotation_id: number) => {
    setQuotationProductsLoading(true);
    const response = unwrapResult(
      await dispatch(
        quotationActions.getTemplateDetailsThunk({
          template_id: Number(internal_quotation_id)
        })
      )
    );

    if (!response?.success) {
      snackBar.show({
        severity: 'error',
        message: 'Error Fetching Quotation Data'
      });
      setQuotationProductsLoading(false);
      return;
    }
    if (response?.originalData?.data) {
      const origQuotationResponse = response?.originalData?.data;
      const QuoteWithChecker = await fetchQuotationProductsAvailability(
        origQuotationResponse
      );
      origQuotationResponse.products =
        QuoteWithChecker?.originalData?.quote_items;
      setQuotationResponse(origQuotationResponse);
      onBranchFieldSetBasedOnQuotation(origQuotationResponse);

      formik.setFieldValue('total_amount', origQuotationResponse?.total_amount);
    }

    setQuotationProductsLoading(false);
  };

  const setInitialFormValues = (
    someValuesAsideConstInitialValues: FormValues
  ) => {
    formik.setValues({
      ...initialValues,
      ...someValuesAsideConstInitialValues
    });

    if (someValuesAsideConstInitialValues?.internal_quotation_id) {
      fetchQuotationProducts(
        someValuesAsideConstInitialValues?.internal_quotation_id
      );
    }
  };

  const resetState = () => {
    formik.resetForm();
  };

  // When adding a taptap customer some fields have conditions that need to be met before they can appear
  // Here, what we do is remove the value of the field if the condition is not met
  const isSomeConditionalFieldsHaveUselessValues = () => {
    const currentFormikValues: any = cloneDeep(formik?.values);
    taptapAddCustomerFields?.forEach((field) => {
      if (field?.conditional) {
        const dependentValue = currentFormikValues[field.conditional.dependsOn];
        if (!field?.conditional?.value?.includes(dependentValue)) {
          formik.setFieldValue(field.key, initialValues[field.key]);
          currentFormikValues[field.key] = initialValues[field.key];
        }
      }
    });

    return currentFormikValues;
  };

  const handleFormSubmit = async (values: any) => {
    if (isLoading.current) {
      return;
    }

    isLoading.current = true;

    // Check if formik fields are valid
    if (formik?.errors && Object.keys(formik?.errors)?.length > 0) {
      isLoading.current = false;

      snackBar.show({
        severity: 'error',
        message: JSON.stringify(formik?.errors)
      });
      return;
    }

    let newData: BranchQuestionaireFormValues = values;

    console.log(newData);

    const { data, error } = await SupabaseClientService.from(
      'branch_questionaire'
    )
      .insert({ ...newData })
      .select('id');

    console.log(data, error);
    if (error) {
      snackBar.show({
        severity: 'error',
        message: JSON.stringify(error),
        useSound: false
      });

      isLoading.current = false;

      console.error(error);
      return;
    }

    snackBar.show({
      severity: 'success',
      message: 'Survey added successfully',
      useSound: true
    });

    // if (data?.[0]?.id) {
    // navigate(`/app/customer-taptap/${data?.[0]?.id}`);
    // }

    isLoading.current = false;
    resetState();
  };

  const onDownloadCSV = async () => {
    if (!canDownloadBranchQuestionaireCSV) {
      snackBar.show({
        severity: 'error',
        message: 'You do not have permission to download this CSV',
        useSound: true
      });
      return;
    }
    const { data, error } = await SupabaseClientService.from(
      'branch_questionaire'
    )
      .select()
      .select();

    if (error) {
      snackBar.show({
        useSound: true,
        severity: 'error',
        message: 'Failed at getting questionaire csv'
      });
    } else {
      const convertToPHTime = (utcString: string) => {
        const date = new Date(utcString);
        return date.toLocaleString('en-PH', {
          timeZone: 'Asia/Manila',
          year: 'numeric',
          month: 'long',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });
      };

      const cleanedData = data?.map((row) => {
        const cleanedRow: any = {
          ...row,
          created_at: convertToPHTime(row.created_at)
        };

        // Convert all non-primitive values to string (like arrays or nested JSON)
        for (const key in cleanedRow) {
          const value = cleanedRow[key];
          if (typeof value === 'object' && value !== null) {
            cleanedRow[key] = JSON.stringify(value);
          }
        }

        return cleanedRow;
      });

      const csvString = unparse(cleanedData);

      downloadSupabaseBranchQuestionaireCSV(
        csvString,
        'Branch_Questionaire.csv'
      );
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema: formSchema,
    validateOnBlur: true,
    onSubmit: handleFormSubmit
  });

  // useEffect(() => {
  //   const search = location.search;
  //   const params = new URLSearchParams(search);

  //   const paramsKeyValuePair: any = {};
  //   params.forEach((value, key) => {
  //     paramsKeyValuePair[key] = value;
  //   });

  //   // const validKeys = taptapAddCustomermap((item) => item.key);

  //   // Filter the data object to include only valid keys
  //   const filteredData = Object.keys(paramsKeyValuePair)
  //     .filter((key) => validKeys.includes(key)) // Keep keys that are in the schema
  //     .reduce((acc: any, key) => {
  //       acc[key] = paramsKeyValuePair[key];
  //       return acc;
  //     }, {});

  //   setInitialFormValues(filteredData);
  //   // formik.setValues({ ...initialValues, ...filteredData });
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  return (
    <Page className={classes.root} title="Branches Questionaire">
      <Container
        maxWidth={false}
        style={{
          opacity:
            isLoading.current ||
            quotationProductsLoading ||
            transactionsInfoLoading
              ? 0.3
              : 1
        }}
      >
        {formik?.values?.is_deleted && (
          <Alert
            hidden
            severity="error"
            style={{ marginTop: 10, marginBottom: 10 }}
          >
            <Typography variant="h3">
              CUSTOMER IS ALREADY DELETED. YOU CANNOT EDIT THIS CUSTOMER
              ANYMORE.
            </Typography>
          </Alert>
        )}
        <LoaderBar isLoading={isLoading.current} />
        <Box display="flex" flexDirection="row" justifyContent="space-between">
          <Typography color="textPrimary" gutterBottom variant="h3">
            Branch Questionaire
          </Typography>

          {canDownloadBranchQuestionaireCSV && (
            <Button variant="outlined" color="primary" onClick={onDownloadCSV}>
              Download CSV
            </Button>
          )}
        </Box>

        <Box mt={3}>
          <BranchQuestionaireForm formikHook={formik} />
        </Box>

        <LoadingButton
          fullWidth
          onClick={async () => {
            const validateForm = await formik.validateForm();
            if (validateForm && Object.keys(validateForm).length > 0) {
              const errorFields = Object.keys(validateForm).join(', ');
              const message = `Please check the following fields: ${errorFields}`;
              snackBar.show({ severity: 'error', message, useSound: true });
              return;
            }

            if (isLoading.current) {
              snackBar.show({
                severity: 'error',
                message: 'Currently loading. Please wait.'
              });
              return;
            }

            formik.handleSubmit();
          }}
          color="primary"
          variant="contained"
          height={50}
          disabled={isLoading.current}
          loading={isLoading.current}
          title="Submit Form"
        />
      </Container>

      <AlertDialog
        title="Some items are out of stock"
        isVisible={
          quotationItemsConcerns && quotationItemsConcerns?.length > 0
            ? true
            : false
        }
        subTitle={` ${quotationItemsConcerns?.join(', \r !!! ')}`}
        handleClose={() => setQuotationItemsConcerns(undefined)}
      />
    </Page>
  );
};

export default AddTapTapCustomer;

const formSchema = yup.object().shape({
  branch: yup.string().required('Branch is required'),
  sales_today: yup
    .number()
    .required('Sales Today is required')
    .min(0, 'Sales Today cannot be negative'),
  availment_powered_builds: yup
    .number()
    .required('Availment Powered Builds is required')
    .min(0, 'Availment Powered Builds cannot be negative'),
  availment_promo: yup
    .number()
    .required('Availment Promo is required')
    .min(0, 'Availment Promo cannot be negative'),
  item_find_today: yup.string().notRequired(),

  peripherals_hardsell: yup.array().of(
    yup.object().shape({
      sales: yup
        .string()
        .nullable()
        .required('Sales name is required'),
      quantity: yup
        .number()
        .required('Quantity is required')
        .min(1, 'Quantity cannot be zero or negative')
    })
  ),

  employee_builds_performance: yup.array().of(
    yup.object().shape({
      sales: yup
        .string()
        .nullable()
        .required('Sales name is required'),
      entry_qty: yup
        .number()
        .required('Entry Qty is required')
        .min(0, 'Entry Qty cannot be negative'),
      mid_qty: yup
        .number()
        .required('Mid Qty is required')
        .min(0, 'Mid Qty cannot be negative'),
      high_qty: yup
        .number()
        .required('High Qty is required')
        .min(0, 'High Qty cannot be negative')
    })
  )
});
